import { useEffect, useState } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useGetExperienceChildConfigs } from '@/features/experiences/hooks/useGetExperienceChildConfigs';
import { viewBaseName } from '@/features/experiences/lib/viewName';
import { ExperienceLayouts } from '@/types/gql.generated';
import type { ExperienceChildConfigName } from '@/types/gql.generated';
import { Box, Flex, Text } from '@/ui';
import { LayoutOptionCard } from '../AddView/LayoutOptionCard';
import { SegmentedControl } from '../StylePanel/SegmentedControl';
import { useCustomizeMode } from '../useCustomizeMode';
import {
  getPickerLayouts,
  type KindFilter,
  KINDS,
  LAYOUT_REGISTRY,
  type LayoutFamily,
  type PickerLayoutOption,
} from './layoutRegistry';

type FamilyFilter = 'all' | LayoutFamily;

// plannerCalendar is the legacy alias for the Month layout — the block system
// (and this picker) present it as plannerMonth. Server month child configs are
// still keyed on plannerCalendar, so map it to plannerMonth for the registry
// lookup or the Month/Printable-Month options would drop from the picker.
const toRegistryLayoutId = (layoutId: ExperienceLayouts): ExperienceLayouts =>
  layoutId === ExperienceLayouts.PlannerCalendar
    ? ExperienceLayouts.PlannerMonth
    : layoutId;

const KIND_OPTIONS = [
  ...KINDS.map((k) => ({
    label: k.label,
    value: k.kind as KindFilter,
  })),
  { label: 'All', value: 'all' as KindFilter },
];

const FAMILY_LABELS: Record<LayoutFamily, string> = {
  calendar: 'Calendar',
  cards: 'Cards',
  list: 'List',
};

// A picker item augments a resolved layout option with its display name and the
// server config name needed to create it — the latter undefined for change mode
// (no new child is created). `label` is the unprefixed view name; the kind
// switcher above already says whether an option prints.
type PickerItem = PickerLayoutOption & {
  label: string;
  baseConfig?: ExperienceChildConfigName;
};

// Stable identity for a picker item. Two items can share a layoutId across kinds
// (e.g. interactive Week and printable Week both use PlannerWeek), so id alone is
// ambiguous — key off the server config in create mode, or kind+layout otherwise.
const itemKey = (item: PickerItem): string =>
  item.baseConfig ?? `${item.kind}:${item.id}`;

type Props = {
  mode: 'create' | 'change';
};

export const AddViewPicker = ({ mode }: Props) => {
  const { experience } = usePageExperienceContext();
  const { layout, selectConfig, selectChangeLayout, clearLayout } =
    useCustomizeMode();
  const { configs } = useGetExperienceChildConfigs({
    enabled: mode === 'create',
  });

  // In change mode, lock the kind to the child's own kind (interactive vs printable)
  // so the picker only offers layouts of that kind — the child's authoritative
  // `kind`, falling back to its ExperienceType when absent.
  const lockedKind = mode === 'create' ? null : experience.kind;

  const [kind, setKind] = useState<KindFilter>(lockedKind ?? 'all');
  const [familyFilter, setFamilyFilter] = useState<FamilyFilter>('all');

  // The selection lives in the URL (`layout` param). Create keys off the server
  // config; change keys off the layout id (kind locked ⇒ id is unambiguous).
  const selectedConfig = layout?.mode === 'create' ? layout.config : null;
  // Normalize the legacy plannerCalendar alias to plannerMonth so it matches the
  // picker items (which are keyed on the registry id) when a legacy Month child
  // is the current change-layout selection.
  const selectedLayoutId =
    layout?.mode === 'change' && layout.layoutId
      ? toRegistryLayoutId(layout.layoutId)
      : null;
  const isItemSelected = (item: PickerItem): boolean =>
    mode === 'create'
      ? item.baseConfig != null && item.baseConfig === selectedConfig
      : item.id === selectedLayoutId;

  // Build picker items:
  // - create mode: join each server config to the registry via its own layoutId so
  //   only layouts the server can actually create are shown. The kind comes straight
  //   from the config, so a shared planner layout surfaces under both kinds.
  // - change mode: all canonical layouts (filtered to lockedKind below).
  const items: PickerItem[] =
    mode === 'create'
      ? configs.flatMap((c): PickerItem[] => {
          const entry = LAYOUT_REGISTRY[toRegistryLayoutId(c.layoutId)];
          if (!entry) return [];
          return [
            {
              ...entry,
              label: viewBaseName(entry.id),
              kind: c.kind,
              baseConfig: c.name,
            },
          ];
        })
      : getPickerLayouts().map((option) => ({
          ...option,
          label: viewBaseName(option.id),
        }));

  // A server config whose layoutId has no LAYOUT_REGISTRY entry is silently
  // omitted above — the view then can't be created and there's no visible error.
  // Surface the drift so a new server config missing its registry entry is caught.
  useEffect(() => {
    if (mode !== 'create') return;
    const dropped = configs.filter(
      (c) => !LAYOUT_REGISTRY[toRegistryLayoutId(c.layoutId)]
    );
    if (dropped.length > 0) {
      console.warn(
        '[AddViewPicker] Ignoring server child configs with no layout registry entry:',
        dropped.map((c) => `${c.name} (${c.layoutId})`)
      );
    }
  }, [configs, mode]);

  const effectiveKind = lockedKind ?? (kind === 'all' ? undefined : kind);
  const filteredItems = effectiveKind
    ? items.filter((item) => item.kind === effectiveKind)
    : items;

  const availableFamilies = [
    ...new Set(filteredItems.map((i) => i.family)),
  ] as LayoutFamily[];

  // Within a family the options list alphabetically, so neither the server
  // config order (create) nor the registry order (change) leaks into the UI.
  const grouped = availableFamilies
    .filter((f) => familyFilter === 'all' || f === familyFilter)
    .map((family) => ({
      family,
      items: filteredItems
        .filter((item) => item.family === family)
        .sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .filter((g) => g.items.length > 0);

  // Switching kind is an ephemeral filter (not addressable), but it clears the
  // current selection since the picked layout may not exist under the new kind.
  const handleKindChange = (next: KindFilter) => {
    setKind(next);
    setFamilyFilter('all');
    clearLayout();
  };

  // Selection writes to the URL; the top bar (useAddViewFlow) reads it back to
  // drive canConfirm + confirm. Create stores the server config, change the layout id.
  const handleSelect = (item: PickerItem) => {
    if (mode === 'create') {
      if (item.baseConfig) selectConfig(item.baseConfig, item.id, item.kind);
    } else {
      selectChangeLayout(item.id as ExperienceLayouts);
    }
  };

  return (
    <Flex direction="column" flex={1} minH={0} overflowY="auto">
      {/* Kind switcher — create mode only; change mode is locked to child kind */}
      {mode === 'create' && (
        <Box pt={4} px={4}>
          <SegmentedControl
            options={KIND_OPTIONS}
            value={kind}
            onChange={handleKindChange}
          />
        </Box>
      )}

      {/* Structure filter chips — hidden for now (AH-2393): with so few layouts
          the sub-filter isn't earning its space. familyFilter stays pinned to
          'all', so every family renders in the grouped list below. */}
      {/*
      <Flex gap="7px" pt={mode === 'create' ? 3 : 4} px={4} wrap="wrap">
        {(['all', ...availableFamilies] as FamilyFilter[]).map((f) => (
          <Box
            as="button"
            bg={familyFilter === f ? 'ink.strong' : 'white'}
            border="1px solid"
            borderColor={familyFilter === f ? 'ink.strong' : 'line.DEFAULT'}
            borderRadius="ah-lg"
            color={familyFilter === f ? 'white' : 'ink.soft'}
            cursor="pointer"
            fontSize="ah-label"
            fontWeight={600}
            key={f}
            px={3}
            py={1}
            transition="background 0.12s, border-color 0.12s, color 0.12s"
            type="button"
            _hover={
              familyFilter === f ? undefined : { borderColor: 'ink.muted' }
            }
            onClick={() => setFamilyFilter(f)}
          >
            {f === 'all' ? 'All' : FAMILY_LABELS[f]}
          </Box>
        ))}
      </Flex>
      */}

      {/* Layout option list, grouped by structure family */}
      <Box pb={6} pt={mode === 'create' ? 2 : 3} px={4}>
        {grouped.map(({ family, items: groupItems }) => (
          <Box key={family} mb={1}>
            <Text
              color="ink.muted"
              fontSize="10px"
              fontWeight={700}
              letterSpacing="0.12em"
              mb={2}
              mt={3}
              textTransform="uppercase"
            >
              {FAMILY_LABELS[family]}
            </Text>
            {groupItems.map((item) => (
              <LayoutOptionCard
                config={item}
                isSelected={isItemSelected(item)}
                key={itemKey(item)}
                onSelect={() => handleSelect(item)}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Flex>
  );
};
